import { onMounted, onScopeDispose, computed, watch } from 'vue';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { useRoute } from 'vue-router';
import { useAuthStore } from '@/core/stores/auth.store';
import { useFluidStore } from '@/core/stores/fluid.store';
import { useNotificationsStore } from '@/core/stores/notifications.store';
import { useEmotionCheckinStore } from '@/core/stores/emotion-checkin.store';
import { useMicroEducationStore } from '@/core/stores/micro-education.store';
import { APP_ONBOARDING_STATUS } from '@/shared/constants/authentication';
import { useNotificationPresenter } from './useNotificationPresenter';

/** One root listener queues all notification types; the presenter selects the appropriate feature dialog. */
export function useNotificationEvents() {
  const auth = useAuthStore();
  const route = useRoute();
  const fluid = useFluidStore();
  const inbox = useNotificationsStore();
  const checkin = useEmotionCheckinStore();
  const education = useMicroEducationStore();
  const presenter = useNotificationPresenter();
  const busy = computed(() =>
    Boolean(
      !auth.isAuthReady ||
      route.meta.moduleTag === 'auth' ||
      checkin.checking ||
      checkin.open ||
      education.open ||
      inbox.centerOpen ||
      fluid.prompt ||
      inbox.selected,
    ),
  );
  const pending = [];
  const handles = [];
  const handled = new Set();
  let disposed = false;
  let presenting = false;

  async function clearDeliveredNotification(itemUnique) {
    const platform = Capacitor.getPlatform();
    if (platform === 'web') {
      return;
    }
    const session = auth.authStateVersion;
    try {
      const delivered = await PushNotifications.getDeliveredNotifications();
      if (disposed || session !== auth.authStateVersion) {
        return;
      }
      const notifications = delivered.notifications.filter((notification) => {
        // Android exposes the FCM tag, not custom message data, in its tray listing.
        if (platform === 'android') {
          return notification.tag === itemUnique;
        }
        return (
          notification.data?.notificationUnique === itemUnique &&
          notification.data?.mainUnique === auth.account.mainUnique
        );
      });
      if (notifications.length) {
        await PushNotifications.removeDeliveredNotifications({ notifications });
      }
    } catch {
      // The server already accepted Snooze; device cleanup cannot undo that response.
      console.warn('[Notifications] Device notification could not be cleared');
    }
  }

  async function present() {
    if (
      presenting ||
      busy.value ||
      !auth.isLoggedIn ||
      auth.appOnboardingStatus !== APP_ONBOARDING_STATUS.COMPLETED
    ) {
      return;
    }
    presenting = true;
    const session = auth.authStateVersion;
    try {
      while (pending.length && !disposed && session === auth.authStateVersion) {
        const event = pending.shift();
        const id = event.data.notificationUnique;
        if (
          event.data.mainUnique !== auth.account.mainUnique ||
          (!event.tapped && handled.has(event.key))
        ) {
          continue;
        }
        await inbox.refreshCount();
        if (disposed || session !== auth.authStateVersion) {
          return;
        }
        const shown = await presenter.present(id, {
          fromPush: true,
          tapped: event.tapped,
          canPresent: () => !busy.value,
        });
        if (disposed || session !== auth.authStateVersion) {
          return;
        }
        if (!shown && busy.value) {
          // A higher-priority dialog opened during the read. Keep this event until it closes.
          pending.unshift(event);
          return;
        }
        handled.add(event.key);
        if (handled.size > 100) {
          handled.delete(handled.values().next().value);
        }
        if (shown) {
          return;
        }
      }
    } finally {
      presenting = false;
      if (pending.length && !busy.value && auth.isLoggedIn) {
        presentPending();
      }
    }
  }

  function presentPending() {
    // Native callbacks have no form to receive rejection. The saved inbox remains available for manual retry.
    void present().catch(() => console.warn('[Notifications] Message could not be opened'));
  }

  function receive(value, tapped = false) {
    if (!value.data?.notificationUnique) {
      return;
    }
    const key = JSON.stringify([value.data.notificationUnique, value.data.deliveryUnique ?? '']);
    const queued = pending.find((event) => event.key === key);
    if (queued) {
      queued.tapped ||= tapped;
    } else {
      pending.push({ data: value.data, tapped, key });
      if (pending.length > 100) {
        pending.shift();
      }
    }
    presentPending();
  }

  watch(
    () => [auth.authStateVersion, auth.isLoggedIn],
    (_value, previous) => {
      // Keep a cold-start tap through login; discard events from a retiring signed-in session.
      if (previous[1]) {
        pending.length = 0;
        handled.clear();
      }
      presentPending();
    },
  );
  watch(() => auth.appOnboardingStatus, presentPending);
  watch(busy, (value) => {
    if (!value) {
      presentPending();
    }
  });

  onMounted(async () => {
    if (Capacitor.getPlatform() === 'web') {
      return;
    }
    for (const [event, callback] of [
      ['pushNotificationReceived', receive],
      ['pushNotificationActionPerformed', (value) => receive(value.notification, true)],
    ]) {
      const handle = await PushNotifications.addListener(event, callback);
      if (disposed) {
        await handle.remove();
      } else {
        handles.push(handle);
      }
    }
  });

  onScopeDispose(() => {
    disposed = true;
    pending.length = 0;
    for (const handle of handles) {
      void handle.remove();
    }
  });

  return { clearDeliveredNotification };
}
